import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const MASTERY_COLORS = ["#EF4444", "#F59E0B", "#FCD34D", "#60A5FA", "#10B981"];
const DIFF_COLORS = { Easy: "#10B981", Average: "#2563EB", Difficult: "#DC2626" };

export function LearningCharts({ data, testIdPrefix = "" }) {
  if (!data) return null;
  const hasSubjects = (data.accuracy_by_subject || []).length > 0;
  const hasTrend = (data.accuracy_trend || []).length > 0;
  const hasDiff = (data.accuracy_by_difficulty || []).some((d) => d.attempts > 0);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}learning-charts`}>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200" data-testid={`${testIdPrefix}chart-accuracy-by-subject`}>
          <h3 className="font-display font-bold text-base">Accuracy by Subject</h3>
          <p className="text-xs text-slate-500 mb-3">How you're performing per subject</p>
          <div className="h-56">
            {hasSubjects ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.accuracy_by_subject}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke="#94A3B8" fontSize={11} domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="accuracy" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No subject data yet" />
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-200" data-testid={`${testIdPrefix}chart-mastery-distribution`}>
          <h3 className="font-display font-bold text-base">Mastery Distribution</h3>
          <p className="text-xs text-slate-500 mb-3">Questions at each mastery level</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.mastery_distribution || []}
                  dataKey="count"
                  nameKey="level"
                  outerRadius={80}
                  innerRadius={40}
                  label={(p) => (p.count > 0 ? `${p.level}: ${p.count}` : "")}
                >
                  {(data.mastery_distribution || []).map((_, i) => (
                    <Cell key={i} fill={MASTERY_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200" data-testid={`${testIdPrefix}chart-accuracy-trend`}>
          <h3 className="font-display font-bold text-base">Accuracy Trend</h3>
          <p className="text-xs text-slate-500 mb-3">Smart Review sessions over time</p>
          <div className="h-56">
            {hasTrend ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.accuracy_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="session" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="accuracy" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="Complete a Smart Review session to see your trend" />
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-200" data-testid={`${testIdPrefix}chart-by-difficulty`}>
          <h3 className="font-display font-bold text-base">Accuracy by Difficulty</h3>
          <p className="text-xs text-slate-500 mb-3">Easy vs Average vs Difficult</p>
          <div className="h-56">
            {hasDiff ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.accuracy_by_difficulty}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={11} domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
                    {(data.accuracy_by_difficulty || []).map((d, i) => (
                      <Cell key={i} fill={DIFF_COLORS[d.name] || "#2563EB"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart label="No difficulty data yet" />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-full grid place-items-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-md">
      {label}
    </div>
  );
}
